class SharedRadialColorPicker {
  constructor () {
    this.activeField = null
    this.isOpen = false
    this.isDragging = false

    this.size = 280
    this.renderScale = 4
    this.renderSize = this.size * this.renderScale
    this.outerR = this.size * 0.48

    this.currentHex = '#FF0000'
    this.angle = -Math.PI / 2
    this.radiusT = 0.5

    this.handleOutsidePointerDown = this.handleOutsidePointerDown.bind(this)
    this.handleWindowResize = this.handleWindowResize.bind(this)
    this.handleWindowScroll = this.handleWindowScroll.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)

    this.ensureBuilt()
  }

  ensureBuilt () {
    if (this.element) return

    this.element = document.createElement('div')
    this.element.className = 'shared-radial-color-picker'
    this.element.hidden = true

    this.element.innerHTML = `
      <div class="shared-radial-color-picker__shell">
        <div class="shared-radial-color-picker__stage">
          <canvas class="shared-radial-color-picker__canvas" width="${this.size}" height="${this.size}"></canvas>
          <div class="shared-radial-color-picker__center-swatch"></div>
          <div class="shared-radial-color-picker__knob"></div>
        </div>
      </div>
    `

    this.canvas = this.element.querySelector('.shared-radial-color-picker__canvas')
    this.ctx = this.canvas.getContext('2d', { alpha: true })
    this.centerSwatch = this.element.querySelector('.shared-radial-color-picker__center-swatch')
    this.knob = this.element.querySelector('.shared-radial-color-picker__knob')

    this.canvas.style.width = `${this.size}px`
    this.canvas.style.height = `${this.size}px`

    this.canvas.addEventListener('pointerdown', (event) => this.onPointerDown(event))
  }

  getHostElement () {
    return this.activeField?.hostElement || document.body
  }

  mountIntoHost () {
    const host = this.getHostElement()

    if (this.element.parentNode !== host) {
      host.appendChild(this.element)
    }
  }

  openFor (fieldController) {
    this.ensureBuilt()

    this.activeField = fieldController
    this.mountIntoHost()

    const value = this.normalizeHex(fieldController.value) || '#FF0000'
    const anchorRect = fieldController.anchorElement.getBoundingClientRect()

    this.currentHex = value
    this.syncPositionFromHex(value)
    this.positionAroundAnchor(anchorRect)
    this.updateCenterSwatch(anchorRect)
    this.drawWheel(anchorRect)
    this.updateKnob()

    this.element.hidden = false
    this.isOpen = true
    this.isDragging = false

    this.element.classList.remove('is-visible')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.element && !this.element.hidden) {
          this.element.classList.add('is-visible')
        }
      })
    })

    this.addGlobalListeners()
  }

  close () {
    this.isOpen = false
    this.isDragging = false
    this.activeField = null

    if (this.element) {
      this.element.classList.remove('is-visible')
      this.element.hidden = true
    }

    this.removeGlobalListeners()
  }

  addGlobalListeners () {
    document.addEventListener('pointerdown', this.handleOutsidePointerDown)
    window.addEventListener('resize', this.handleWindowResize)
    window.addEventListener('scroll', this.handleWindowScroll, true)
    window.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('keydown', this.onKeyDown)
  }

  removeGlobalListeners () {
    document.removeEventListener('pointerdown', this.handleOutsidePointerDown)
    window.removeEventListener('resize', this.handleWindowResize)
    window.removeEventListener('scroll', this.handleWindowScroll, true)
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('keydown', this.onKeyDown)
  }

  handleOutsidePointerDown (event) {
    if (!this.isOpen || !this.activeField) return

    const clickedInsidePicker = this.element.contains(event.target)
    const clickedAnchor = this.activeField.anchorElement.contains(event.target)

    if (clickedInsidePicker || clickedAnchor) return
    this.close()
  }

  handleWindowResize () {
    if (!this.isOpen || !this.activeField) return

    const anchorRect = this.activeField.anchorElement.getBoundingClientRect()
    this.mountIntoHost()
    this.positionAroundAnchor(anchorRect)
    this.updateCenterSwatch(anchorRect)
    this.drawWheel(anchorRect)
    this.updateKnob()
  }

  handleWindowScroll () {
    if (!this.isOpen || !this.activeField) return

    const anchorRect = this.activeField.anchorElement.getBoundingClientRect()
    this.positionAroundAnchor(anchorRect)
    this.updateCenterSwatch(anchorRect)
  }

  onKeyDown (event) {
    if (event.key === 'Escape') this.close()
  }

  onPointerDown (event) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = true
    this.setFromPointer(event.clientX, event.clientY, false)
  }

  onPointerMove (event) {
    if (!this.isDragging) return
    this.setFromPointer(event.clientX, event.clientY, false)
  }

  onPointerUp () {
    if (!this.isDragging) return
    this.isDragging = false
    this.setFromPointer(event.clientX, event.clientY, true)
    this.close()
  }

  positionAroundAnchor (anchorRect) {
    const left = anchorRect.left + anchorRect.width / 2 - this.size / 2
    const top = anchorRect.top + anchorRect.height / 2 - this.size / 2

    this.element.style.left = `${left}px`
    this.element.style.top = `${top}px`
  }

  updateCenterSwatch (anchorRect) {
    const innerSwatchSize = Math.min(anchorRect.width, anchorRect.height)

    this.centerSwatch.style.width = `${innerSwatchSize}px`
    this.centerSwatch.style.height = `${innerSwatchSize}px`
    this.centerSwatch.style.background = this.currentHex
  }

  drawWheel (anchorRect) {
    const triggerRadius = Math.min(anchorRect.width, anchorRect.height) / 2
    const renderTriggerRadius = triggerRadius * this.renderScale
    const renderOuterR = this.outerR * this.renderScale

    const offscreen = document.createElement('canvas')
    offscreen.width = this.renderSize
    offscreen.height = this.renderSize
    const offCtx = offscreen.getContext('2d', { alpha: true })

    const image = offCtx.createImageData(this.renderSize, this.renderSize)
    const data = image.data
    const cx = this.renderSize / 2
    const cy = this.renderSize / 2

    for (let y = 0; y < this.renderSize; y++) {
      for (let x = 0; x < this.renderSize; x++) {
        const dx = x - cx
        const dy = y - cy
        const dist = Math.hypot(dx, dy)
        const idx = (y * this.renderSize + x) * 4

        if (dist < renderTriggerRadius || dist > renderOuterR) {
          data[idx + 3] = 0
          continue
        }

        const theta = Math.atan2(dy, dx)
        const t = (dist - renderTriggerRadius) / (renderOuterR - renderTriggerRadius)
        const color = this.radialColor(theta, t)

        data[idx] = color.r
        data[idx + 1] = color.g
        data[idx + 2] = color.b
        data[idx + 3] = 255
      }
    }

    offCtx.putImageData(image, 0, 0)

    const cxVisible = this.size / 2
    const cyVisible = this.size / 2

    this.ctx.clearRect(0, 0, this.size, this.size)

    this.ctx.save()
    this.ctx.beginPath()
    this.ctx.arc(cxVisible, cyVisible, this.outerR, 0, Math.PI * 2)
    this.ctx.arc(cxVisible, cyVisible, triggerRadius, 0, Math.PI * 2, true)
    this.ctx.closePath()
    this.ctx.clip()
    this.ctx.imageSmoothingEnabled = true
    this.ctx.drawImage(offscreen, 0, 0, this.size, this.size)
    this.ctx.restore()

    this.ctx.save()
    this.ctx.beginPath()
    this.ctx.arc(cxVisible, cyVisible, this.outerR + 0.35, 0, Math.PI * 2)
    this.ctx.arc(cxVisible, cyVisible, Math.max(triggerRadius - 0.35, 0), 0, Math.PI * 2, true)
    this.ctx.closePath()
    this.ctx.lineWidth = 1.2
    this.ctx.strokeStyle = 'rgba(255,255,255,0.14)'
    this.ctx.shadowColor = 'rgba(255,255,255,0.05)'
    this.ctx.shadowBlur = 1.5
    this.ctx.stroke()
    this.ctx.restore()
  }

  setFromPointer (clientX, clientY, commit = false) {
    if (!this.activeField) return

    const anchorRect = this.activeField.anchorElement.getBoundingClientRect()
    const triggerRadius = Math.min(anchorRect.width, anchorRect.height) / 2

    const rect = this.canvas.getBoundingClientRect()
    const x = ((clientX - rect.left) / rect.width) * this.size
    const y = ((clientY - rect.top) / rect.height) * this.size
    const cx = this.size / 2
    const cy = this.size / 2
    const dx = x - cx
    const dy = y - cy
    const dist = Math.hypot(dx, dy)

    this.angle = Math.atan2(dy, dx)

    const clamped = Math.min(this.outerR, Math.max(triggerRadius, dist))
    this.radiusT = (clamped - triggerRadius) / (this.outerR - triggerRadius)

    const color = this.radialColor(this.angle, this.radiusT)
    const hex = this.rgbToHex(color)

    this.currentHex = hex
    this.updateCenterSwatch(anchorRect)
    this.updateKnob()
    this.activeField.setValue(hex, { emitInput: true, emitChange: commit })
  }

  updateKnob () {
    if (!this.activeField) return

    const anchorRect = this.activeField.anchorElement.getBoundingClientRect()
    const triggerRadius = Math.min(anchorRect.width, anchorRect.height) / 2

    const cx = this.size / 2
    const cy = this.size / 2
    const r = triggerRadius + this.radiusT * (this.outerR - triggerRadius)
    const x = cx + Math.cos(this.angle) * r
    const y = cy + Math.sin(this.angle) * r

    this.knob.style.left = `${x}px`
    this.knob.style.top = `${y}px`
    this.knob.style.background = this.currentHex
  }

  radialColor (theta, t) {
    const hue = ((theta * 180 / Math.PI) + 90 + 360) % 360
    const pure = this.hsvToRgb(hue, 1, 1)

    if (t <= 0.5) {
      const k = t / 0.5
      return {
        r: this.mix(0, pure.r, k),
        g: this.mix(0, pure.g, k),
        b: this.mix(0, pure.b, k)
      }
    }

    const k = (t - 0.5) / 0.5
    return {
      r: this.mix(pure.r, 255, k),
      g: this.mix(pure.g, 255, k),
      b: this.mix(pure.b, 255, k)
    }
  }

  hsvToRgb (h, s, v) {
    const c = v * s
    const x = c * (1 - Math.abs((h / 60) % 2 - 1))
    const m = v - c
    let r = 0
    let g = 0
    let b = 0

    if (h >= 0 && h < 60) [r, g, b] = [c, x, 0]
    else if (h < 120) [r, g, b] = [x, c, 0]
    else if (h < 180) [r, g, b] = [0, c, x]
    else if (h < 240) [r, g, b] = [0, x, c]
    else if (h < 300) [r, g, b] = [x, 0, c]
    else [r, g, b] = [c, 0, x]

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255)
    }
  }

  rgbToHex ({ r, g, b }) {
    return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase()}`
  }

  hexToRgb (hex) {
    const normalized = this.normalizeHex(hex)
    if (!normalized) return null

    const raw = normalized.slice(1)
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16)
    }
  }

  rgbToHsv (r, g, b) {
    r /= 255
    g /= 255
    b /= 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const d = max - min
    let h = 0

    if (d !== 0) {
      if (max === r) h = ((g - b) / d) % 6
      else if (max === g) h = (b - r) / d + 2
      else h = (r - g) / d + 4

      h *= 60
      if (h < 0) h += 360
    }

    return {
      h,
      s: max === 0 ? 0 : d / max,
      v: max
    }
  }

  syncPositionFromHex (hex) {
    const rgb = this.hexToRgb(hex)
    if (!rgb) {
      this.angle = -Math.PI / 2
      this.radiusT = 0.5
      return
    }

    const hsv = this.rgbToHsv(rgb.r, rgb.g, rgb.b)
    this.angle = ((hsv.h - 90) * Math.PI) / 180
    this.radiusT = Math.max(0, Math.min(1, hsv.v))
  }

  mix (a, b, t) {
    return Math.round(a + (b - a) * t)
  }

  normalizeHex (value) {
    if (!value) return null

    const hex = value.trim()
    if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return null

    if (hex.length === 4) {
      return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toUpperCase()
    }

    return hex.toUpperCase()
  }
}

export const sharedRadialColorPicker = new SharedRadialColorPicker()
